// src/pages/TeamVisibility/TeamVisibility.tsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { visibilityApi, type TeamMember } from "../../api/todo";
import { getAllUsers } from "../../api/user";
import { useAuth } from "../../contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, ChevronDown, ChevronRight, Briefcase, X, Search, AlertTriangle, Clock, ArrowLeft,
} from "lucide-react";

const ELEVATED = new Set(["admin", "owner", "superadmin", "founder", "director"]);
const MANAGER_ROLES = new Set(["manager", "supervisor", "team lead", "hod", "hr"]);

function toMember(u: any): TeamMember {
  return {
    user_id: u.user_id || u.id,
    employee_id: u.employee_id || u.id,
    name: u.name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email,
    email: u.email || null,
    role: u.role || null,
    designation: u.designation || null,
    department_id: u.department_id || null,
    manager_id: u.manager_id || null,
    profile_photo_url: u.profile_photo_url || null,
  };
}

const STATUS_PILLS: Record<string, string> = {
  PENDING:     "gt-pill is-pending",
  TODO:        "gt-pill is-pending",
  IN_PROGRESS: "gt-pill is-progress",
  COMPLETED:   "gt-pill is-done",
  DONE:        "gt-pill is-done",
  IN_REVIEW:   "gt-pill is-high",
  BLOCKED:     "gt-pill is-overdue",
  MISSED:      "gt-pill is-overdue",
  CANCELLED:   "gt-pill is-ghost",
};

const PRIORITY_PILLS: Record<string, string> = {
  URGENT: "gt-pill is-urgent",
  HIGH:   "gt-pill is-high",
  MEDIUM: "gt-pill is-medium",
  LOW:    "gt-pill is-low",
};

function EmployeeDetail({ member }: { member: TeamMember }) {
  const navigate = useNavigate();
  const [personal, setPersonal] = useState<any[]>([]);
  const [assigned, setAssigned] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    visibilityApi
      .userToday(member.user_id)
      .then((r) => {
        setPersonal(r.data.personal || []);
        setAssigned(r.data.assigned || []);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Could not load tasks"))
      .finally(() => setLoading(false));
  }, [member.user_id]);

  const all = [
    ...personal.map((t) => ({ ...t, _src: "Personal" })),
    ...assigned.map((t) => ({ ...t, _src: "Assigned" })),
  ];

  return (
    <div className="px-6 pb-5 pt-4" style={{ background: 'var(--gt-surface-rail-soft)', borderTop: '1px solid var(--gt-divider)' }}>
      <div className="flex items-center gap-2 mb-3">
        <span className="gt-leaf" />
        <span className="gt-eyebrow">Today's Tasks</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-2 gt-page-sub">
          <div className="gt-loading-ring" style={{ width: 16, height: 16, borderWidth: 2 }} />
          <span>Loading…</span>
        </div>
      ) : error ? (
        <div className="gt-pill is-overdue">
          <AlertTriangle className="w-3 h-3" /> {error}
        </div>
      ) : all.length === 0 ? (
        <p className="gt-page-sub gt-serif-italic">No tasks scheduled for today.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {all.map((t) => (
            <div
              key={t.id}
              onClick={() => t._src === "Assigned" && navigate(`/tasks/${t.id}`)}
              className={`gt-card p-3 ${t._src === "Assigned" ? "cursor-pointer" : ""}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="gt-display flex-1 line-clamp-2" style={{ fontSize: 13, fontWeight: 600, color: 'var(--gt-text-primary)', lineHeight: 1.35 }}>
                  {t.title}
                </p>
                <span className={STATUS_PILLS[t.status] || STATUS_PILLS.PENDING} style={{ flexShrink: 0 }}>
                  {(t.status || "").replace(/_/g, " ")}
                </span>
              </div>

              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`gt-pill ${t._src === "Personal" ? "is-personal" : "is-assigned"}`}>
                  {t._src === "Personal" ? "Personal" : (<><Briefcase className="w-3 h-3" /> Assigned</>)}
                </span>
                {t.priority && (
                  <span className={PRIORITY_PILLS[t.priority] || "gt-pill is-ghost"}>
                    <span className="gt-dot" />
                    {t.priority}
                  </span>
                )}
                {t.due_date && (
                  <span className="gt-pill is-ghost gt-numerals">
                    <Clock className="w-3 h-3" />
                    {t.due_date.slice(0, 10)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => navigate(`/tasks/list?assigned_to=${member.user_id}&include_closed=true`)}
        className="gt-btn is-soft is-sm mt-4"
      >
        View all tasks for {member.name}
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function TeamVisibility() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = (user?.role || "").toLowerCase();
  const canAccess = ELEVATED.has(role) || MANAGER_ROLES.has(role);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await visibilityApi.team();
      let list: TeamMember[] = res.data.items || [];

      if (list.length === 0) {
        try {
          const usersRes = await getAllUsers();
          const raw = usersRes.users || [];
          list = raw
            .filter((u) => (u.user_id || u.id) !== (user?.id || user?.sub))
            .map(toMember);
        } catch {
          list = [];
        }
      }

      setMembers(list);
    } catch {
      try {
        const usersRes = await getAllUsers();
        const raw = usersRes.users || [];
        setMembers(
          raw
            .filter((u) => (u.user_id || u.id) !== (user?.id || user?.sub))
            .map(toMember)
        );
      } catch {
        setMembers([]);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  if (!canAccess) {
    return (
      <div className="gt-shell flex items-center justify-center min-h-[60vh]">
        <div className="gt-card p-8 max-w-sm text-center" style={{ borderColor: 'rgba(220,38,38,0.18)' }}>
          <div className="inline-flex items-center justify-center mb-3" style={{ width: 48, height: 48, borderRadius: 999, background: 'var(--gt-status-overdue-bg)', color: 'var(--gt-prio-urgent-fg)' }}>
            <X className="w-6 h-6" />
          </div>
          <h2 className="gt-display" style={{ fontSize: 16, color: 'var(--gt-prio-urgent-fg)' }}>Access <em className="gt-serif-italic">Denied</em></h2>
          <p className="gt-page-sub mt-2">Only managers and admins can view employee task visibility.</p>
        </div>
      </div>
    );
  }

  const filtered = members.filter((m) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (m.name || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q) ||
      (m.designation || "").toLowerCase().includes(q) ||
      (m.role || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        <button onClick={() => navigate(-1)} className="gt-btn is-ghost is-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Daily Planner
        </button>

        {/* Header */}
        <div className="gt-page-hero flex-col md:flex-row gap-4">
          <div>
            <div className="gt-eyebrow-row">
              <span className="gt-leaf" />
              <span className="gt-eyebrow">i. Visibility</span>
            </div>
            <h1 className="gt-page-title">
              Employee <em>Tasks</em>
            </h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              {loading
                ? <>Loading <em className="gt-serif-italic">team</em>…</>
                : <><span className="gt-display gt-numerals" style={{ color: 'var(--gt-text-primary)' }}>{filtered.length}</span> employee{filtered.length !== 1 ? "s" : ""} — click any row to see <em className="gt-serif-italic">today's</em> tasks.</>}
            </p>
          </div>

          <div className="gt-search-wrap" style={{ width: 280 }}>
            <Search />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, role…"
              className="gt-input is-search"
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="gt-loading-ring" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="gt-card p-12 text-center">
            <div className="gt-empty">
              <div className="gt-empty-leaf"><Users className="w-6 h-6" /></div>
              <div className="gt-empty-title">No employees found</div>
              <div className="gt-page-sub">{search ? "Try a different search term." : "No team members are visible for your role."}</div>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            {filtered.map((m) => {
              const isExpanded = expanded === m.user_id;
              const initials = (m.name || "?")
                .split(" ")
                .map((w) => w[0])
                .slice(0, 2)
                .join("")
                .toUpperCase();

              return (
                <motion.div
                  key={m.user_id}
                  layout
                  className="gt-card overflow-hidden"
                  style={{ padding: 0 }}
                >
                  <button
                    onClick={() => setExpanded(isExpanded ? null : m.user_id)}
                    className="w-full flex items-center gap-4 px-5 py-4 transition-colors text-left"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--gt-surface-rail-soft)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div className="gt-avatar shrink-0" style={{ width: 42, height: 42, fontSize: 13 }}>
                      {m.profile_photo_url ? (
                        <img src={m.profile_photo_url} alt="" className="w-full h-full object-cover rounded-full" />
                      ) : (
                        initials
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="gt-display truncate" style={{ fontSize: 14, fontWeight: 700, color: 'var(--gt-text-primary)' }}>
                        {m.name || m.email || "Unknown"}
                      </p>
                      <p className="gt-page-sub truncate" style={{ marginTop: 2 }}>
                        <span className="gt-eyebrow" style={{ marginBottom: 0, fontSize: 9.5 }}>
                          {m.designation || m.role || "Employee"}
                        </span>
                        {m.email && <span style={{ marginLeft: 8 }}>· {m.email}</span>}
                      </p>
                    </div>

                    <div className="shrink-0">
                      <span className="gt-icon-btn is-sm" style={{ pointerEvents: 'none' }}>
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </span>
                    </div>
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        key="detail"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        className="overflow-hidden"
                      >
                        <EmployeeDetail member={m} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
